import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';

import useErrorManager from "../../../hooks/useErrorManager";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

import GenericDetailsTable from "../../Layout/Table/GenericDetailsTable";
import GenericDatePicker from "../../Layout/GenericDatePicker";
import GenericTimePicker from "../../Layout/GenericTimePicker";

import { ExpandMore, Delete } from '@mui/icons-material';

import { TextField, Button, Box, Tooltip, Typography, CircularProgress, Grid, Alert, MenuItem, IconButton, Accordion, AccordionSummary, AccordionDetails  } from "@mui/material";

import { useNotification } from '../../../contexts/NotificationContext';

const DEFAULT_READOUT_DETAILS = {
    type: "Recurring",
    monthlyRecurrenceType: "DayInMonth",
    commonCredentials: false,
    description: "",
    temporaryForwardingAssignment: null,
    networks: []
};

const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

const StyledFormTextField = styled(TextField)`
  margin-top: 4px;
  margin-bottom: 4px;
`;

const NetworksReadout = forwardRef(({ gwDetails, setIsValid, readoutType }, ref) => {
    const { gatewayId } = useParams();

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();

    const axiosInstance = useAxiosPrivate();
    const navigate = useNavigate();

    const [networks, setNetworks] = useState([]);
    const [networksRODetails, setNetworksRODetails] = useState(DEFAULT_READOUT_DETAILS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [gatewayId]);

    const fetchData = async () => {
        setLoading(true);
        await Promise.all([fetchNetworksScanByGateway(), fetchReadoutDetails()]);
        setLoading(false);
    };
    
    const fetchNetworksScanByGateway = async () => {
        try {
            const response = await axiosInstance.get(`/net/scan/${gatewayId}/unflattened`, {
                withCredentials: true,
            });
            setNetworks(response.data);
            clearError('fetch');
        } catch (error) {
            setError('fetch', error.message);
            showNotification('Failed to fetch network scan for the gateway', 'error');
        }
    };

    const fetchReadoutDetails = async () => {
        try {
            const endpoint = readoutType === "Maintenance" 
                ? `/net/readout/${gatewayId}/maintenance` 
                : `/net/readout/${gatewayId}/recurring`;
            const response = await axiosInstance.get(endpoint);
            // Default monthly recurrence type and mode if not provided
            response.data.monthlyRecurrenceType = "DayInMonth";
            response.data.commonCredentials = false;
            setNetworksRODetails(response.data || DEFAULT_READOUT_DETAILS);
            validateForm(response.data || DEFAULT_READOUT_DETAILS);
            clearError('fetchReadoutDetails');
        } catch (error) {
            setError('fetchReadoutDetails', error.message);
        } 
    };

    

    // Helper function to retrieve network nodes for a given scanned network using networkNumber.
    const getNodesForNetwork = (networkNumber) => {
        const scannedNetwork = networks.find(net => net.networkNumber === Number(networkNumber));
        if (scannedNetwork) {
            return scannedNetwork.nodes;
        }
        return [];
    };

    const isNetworkSelectedInOther = (optionNetworkNumber, currentIndex) => {
        return networksRODetails.networks.some(
          (net, idx) =>
            idx !== currentIndex && Number(net.networkNumber) === Number(optionNetworkNumber)
        );
    };

    // Update a field for a specific network
    const handleNetworkChange = (index, field, value) => {
        const updatedNetworks = networksRODetails.networks.map((net, idx) => {
            if (idx === index) {
                return { ...net, [field]: value, ...(field === 'networkNumber' && { nodeId: 256 }) };
            }
            return net;
        });
        const updatedDetails = { ...networksRODetails, networks: updatedNetworks };
        setNetworksRODetails(updatedDetails);
        validateForm(updatedDetails);
    };

    // Update a date for a given network
    const handleDateTimeChange = (networkIndex, dateIndex, newValue, type) => {
        // Get the current date/time for the specific network date entry
        const currentDateTime = new Date(networksRODetails.networks[networkIndex].taskPlanTimeIdList[dateIndex].time);
      
        let updatedDate;
        if (type === 'date') {
          // Update only the date part and keep the current time
          updatedDate = new Date(
            newValue.year(),
            newValue.month(),
            newValue.date(),
            currentDateTime.getHours(),
            currentDateTime.getMinutes(),
            currentDateTime.getSeconds()
          );
        } else if (type === 'time') {
          // Update only the time part and keep the current date
          updatedDate = new Date(
            currentDateTime.getFullYear(),
            currentDateTime.getMonth(),
            currentDateTime.getDate(),
            newValue.hour(),
            newValue.minute(),
            newValue.second()
          );
        }
      
        // Update the specific network entry with the merged date/time value
        const updatedNetworks = networksRODetails.networks.map((net, idx) => {
          if (idx === networkIndex) {
            const updatedDates = [...net.taskPlanTimeIdList];
            updatedDates[dateIndex] = { ...updatedDates[dateIndex], time: updatedDate,  recurrenceDayInMonth: getRecurrenceDayByDate(updatedDate) };
            return { ...net, taskPlanTimeIdList: updatedDates };
          }
          return net;
        });
        
        const updatedDetails = { ...networksRODetails, networks: updatedNetworks };
        setNetworksRODetails(updatedDetails);
        validateForm(updatedDetails);
    };

    const getRecurrenceDayByDate = (date) => {
        return date.getDate();
    };


    const handleRODetailsChange = (field, value) => {
        setNetworksRODetails(prev => ({
            ...prev,
            [field]: value
        }));
        validateForm({ ...networksRODetails, [field]: value });
    };

   // Add a date entry for a specific network (up to 1 for Maintenance, 2 for Recurring)
    const addDate = (networkIndex) => {
        const updatedNetworks = networksRODetails.networks.map((net, idx) => {
            if (idx === networkIndex) {
                const maxDates = readoutType === "Maintenance" ? 1 : Math.floor(gwDetails.tariffInfo.networkRecurringReadoutsPerYear/12);
                if (net.taskPlanTimeIdList.length < maxDates) {
                    const now = new Date();
                    return {
                        ...net,
                        taskPlanTimeIdList: [
                            ...net.taskPlanTimeIdList,
                            { time: now, recurrenceDayInMonth: getRecurrenceDayByDate(now) }
                        ]
                    };
                }
            }
            return net;
        });
        const updatedDetails = { ...networksRODetails, networks: updatedNetworks };
        setNetworksRODetails(updatedDetails);
        validateForm(updatedDetails);
    };

    // Remove a  date entry for a given network
    const removeDate = (networkIndex, dateIndex) => {
        const updatedNetworks = networksRODetails.networks.map((net, idx) => {
            if (idx === networkIndex) {
                return {
                    ...net,
                    taskPlanTimeIdList: net.taskPlanTimeIdList.filter((_, i) => i !== dateIndex)
                };
            }
            return net;
        });
        const updatedDetails = { ...networksRODetails, networks: updatedNetworks };
        setNetworksRODetails(updatedDetails);
        validateForm(updatedDetails);
    };

    // Add a new network (up to 5)
    const addNetwork = () => {
        if (networksRODetails.networks.length < gwDetails.tariffInfo.networkMaxNumberOfNetworks) {
            const newNetwork = {
                networkNumber: "",
                nodeId: 0,
                username: "",
                password: "",
                taskPlanTimeIdList: []
            };
            const updatedNetworks = [...networksRODetails.networks, newNetwork];
            const updatedDetails = { ...networksRODetails, networks: updatedNetworks };
            setNetworksRODetails(updatedDetails);
            validateForm(updatedDetails);
        }
    };

    // Remove a network
    const removeNetwork = (networkIndex) => {
        const updatedNetworks = networksRODetails.networks.filter((_, idx) => idx !== networkIndex);
        const updatedDetails = { ...networksRODetails, networks: updatedNetworks };
        setNetworksRODetails(updatedDetails);
        validateForm(updatedDetails);
    };

    const validateForm = (data) => {
        let networksValid = data.networks.every(net => {
          // 1. Check that the network number exists in the scanned networks.
          const scanned = networks.find(n => Number(n.networkNumber) === Number(net.networkNumber));
          if (!scanned) return false;
      
          // 2. Check that nodeId is either 256 or one of the nodes in the scanned network.
          if (Number(net.nodeId) !== 256) {
            if (!scanned.nodes || !scanned.nodes.some(node => Number(node.nodeId) === Number(net.nodeId))) {
              return false;
            }
          }
      
          // 3. Check that taskPlanTimeIdList has at least one entry with a valid future time.
          if (!net.taskPlanTimeIdList || net.taskPlanTimeIdList.length === 0) return false;
          const validTime = net.taskPlanTimeIdList.every(entry => entry.time && new Date(entry.time) > new Date());
          if (!validTime) return false;
      
          // 4. Check that username and password are valid non-empty strings.
          if (!net.username || typeof net.username !== 'string' || net.username.trim() === '') return false;
          if (!net.password || typeof net.password !== 'string' || net.password.trim() === '') return false;
      
          return true;
        });
        const isValid = data.type !== "" && data.monthlyRecurrenceType !== "" && networksValid;
        setIsValid(isValid);
      };


    useImperativeHandle(ref, () => ({
        saveData: async () => {
            try {
                const payload = {
                    commonCredentials: false, //networksRODetails.commonCredentials,
                    temporaryForwardingProfileId: null,
                    description: networksRODetails.description,
                    type: readoutType, // or explicitly "Maintenance" if that's fixed
                    networks: networksRODetails.networks,
                    // .map(net => ({
                    //   networkNumber: net.networkNumber,
                    //   nodeId: net.nodeId,
                    //   username: net.username,
                    //   password: net.password,
                    //   taskPlanTimeIdList: net.taskPlanTimeIdList.map(entry => ({
                    //     time: entry.time instanceof Date ? entry.time.toISOString() : entry.time,
                    //     recurrenceWeekDay: entry.recurrenceWeekDay,
                    //     recurrenceInstance: entry.recurrenceInstance
                    //   }))
                    // }))
                };
                await axiosInstance.put(`/net/${gatewayId}/readout`, payload);
                clearError('createScan');
                showNotification('Networks Readout saved successfully', 'success');
                // Optionally navigate to another page
                navigate(`/gateways/${gatewayId}`);
            } catch (error) {
                setError('createScan', "There was an error saving networks readout. Please try again later.");
                showNotification('Error saving networks readout', 'error');
            }
        },
    }));

    // const ROInfoRows = [,
    //     { key: "monthlyRecurrenceType", label: "Recurrence Type", render: (item) => item.monthlyRecurrenceType === "DayInMonth" ? "Day in Month" : "Weekday" }
    // ];
    const tariffInfoRows = [
        { key: "tariff", label: "Tariff", render: (item) => item.tariffInfo.asn  },
        { key: "networksNumber", label: "Max Number of Networks", render: (item) => item.tariffInfo.networkMaxNumberOfNetworks  },
        readoutType === "Maintenance"
        ? {
            key: "maintenanceROperYear",
            label: "Maintenance Readouts per Year",
            render: item =>
            `${item.tariffInfo.networkMaintenanceReadoutsPerYear} (${item.usedNetworkMaintenanceReadoutsPerYear || 0} used)`
        }
        : {
            key: "recurringROperYear",
            label: "Recurring Readouts per Year",
            render: item => `${item.tariffInfo.networkRecurringReadoutsPerYear}`
        }
    ];

    if (loading) return <CircularProgress />;
    if (getError('fetchReadoutDetails')) return <Typography variant="h7">Error fetching readout details: {getError('fetchReadoutDetails')}</Typography>;

    return (
        <Box sx={{ marginY: 2 }}>
            <GenericDetailsTable
                detailsValue={gwDetails}
                detailsList={tariffInfoRows} />
            {/* <GenericDetailsTable
                detailsValue={networksRODetails}
                detailsList={ROInfoRows} /> */}

             {/* Render an accordion for each network */}
             {networksRODetails.networks.map((network, networkIndex) => (
                <Accordion key={networkIndex} sx={{ marginY: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                            <Typography variant="subtitle1">NETWORK {networkIndex + 1}</Typography>
                            <IconButton onClick={(e) => { e.stopPropagation(); removeNetwork(networkIndex); }}>
                                <Delete />
                            </IconButton>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            {/* Scanned Network select (using networkNumber) */}
                            <Grid item xs={4}>
                                <StyledFormTextField
                                    label="Network"
                                    select
                                    value={network.networkNumber || ''}
                                    onChange={(e) => handleNetworkChange(networkIndex, 'networkNumber', e.target.value)}
                                    fullWidth
                                    required
                                    margin="normal"
                                >
                                     {networks.map(net => (
                                        <MenuItem key={net.networkNumber} value={net.networkNumber}
                                        disabled={isNetworkSelectedInOther(net.networkNumber, networkIndex)}
                                        >
                                           {net.networkNumber}
                                        </MenuItem>
                                    ))}
                                </StyledFormTextField>
                            </Grid>

                            {/* Network Node select (using nodeId) */}
                            {network.networkNumber !== 0 && (
                                <Grid item xs={4}>
                                    <StyledFormTextField
                                        label="Node"
                                        select
                                        fullWidth
                                        value={network.nodeId || 256}
                                        onChange={(e) => handleNetworkChange(networkIndex, 'nodeId', e.target.value)}
                                        required
                                        margin="normal"
                                    >
                                        <MenuItem key={256} value={256}>
                                            Any
                                        </MenuItem>
                                        {getNodesForNetwork(network.networkNumber).map(node => (
                                            <MenuItem key={node.nodeId} value={node.nodeId}>
                                                {node.nodeId}
                                            </MenuItem>
                                        ))}
                                    </StyledFormTextField>
                                </Grid>
                            )}  
                            {/* Render date pickers */}
                            {network.taskPlanTimeIdList.map((entry, dateIndex) => (
                                <Grid item xs={12} key={dateIndex}>
                                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                        <GenericDatePicker 
                                            initialDate={entry.time} 
                                            onDateChange={(newDate) => handleDateTimeChange(networkIndex, dateIndex, newDate, 'date')} 
                                        />
                                        <GenericTimePicker 
                                            initialTime={entry.time} 
                                            onTimeChange={(newTime) => handleDateTimeChange(networkIndex, dateIndex, newTime, 'time')} 
                                        />
                                        <IconButton onClick={() => removeDate(networkIndex, dateIndex)}>
                                            <Delete />
                                        </IconButton>
                                        {new Date(entry.time) < new Date() && (
                                            <Alert 
                                                severity="error" 
                                                sx={{ display: 'flex', alignItems: 'center', borderRadius: 2 }}
                                            >
                                                Date must not be in the past!
                                            </Alert>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                            {network.taskPlanTimeIdList.length < (readoutType === "Maintenance" ? 1 :  Math.floor(gwDetails.tariffInfo.networkRecurringReadoutsPerYear/12)) && (
                                <Grid item xs={12}>
                                    <Button variant="contained" color="primary" onClick={() => addDate(networkIndex)}>
                                        ADD DATE
                                    </Button>
                                </Grid>
                            )}
                            {/* Placeholder for additional network-specific fields */}
                            <Grid item xs={4}>
                                <StyledFormTextField
                                    label="Username"
                                    value={network.username}
                                    onChange={(e) =>
                                        handleNetworkChange(networkIndex, 'username', e.target.value)
                                    }
                                    fullWidth
                                    margin="normal"
                                    required
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <StyledFormTextField
                                    label="Password"
                                    value={network.password}
                                    onChange={(e) =>
                                        handleNetworkChange(networkIndex, 'password', e.target.value)
                                    }
                                    fullWidth
                                    margin="normal"
                                    type="password"
                                    required
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}
            {networksRODetails.networks.length < gwDetails.tariffInfo.networkMaxNumberOfNetworks && (
                <Box sx={{ marginY: 2 }}>
                    <Tooltip title={!networks ? "No networks available, please scan first" : ""} arrow>
                        <span>
                            <Button variant="contained" color="primary" onClick={addNetwork} disabled={!networks}>
                                ADD NETWORK
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
});

export default NetworksReadout;
